import React, { useState } from "react";
import Layout from "../components/layout/Layout";
import { Mail, Phone, MapPin, Send, icons } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useContentImages } from "@/hooks/useContentImages";
import { useContactDetails } from "@/hooks/useContactDetails";

const MAX_MESSAGE_LENGTH = 1500;

const Contact = () => {
  const { toast } = useToast();
  const { images: aboutImages } = useContentImages("contact");
  const heroImage = aboutImages[0];
  const { getEmails, getPhones, getAddresses, getSocialLinks } =
    useContactDetails("contact");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Enforce character limit for message
    if (name === "message" && value.length > MAX_MESSAGE_LENGTH) {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    // Get first email from contact details, fallback to default
    const emails = getEmails();
    const toEmail = emails.length > 0 ? emails[0].value : "info@goaltec.com";

    // Construct email body
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || "Not provided"}
Company: ${formData.company || "Not provided"}

Message:
${formData.message}
    `.trim();

    const mailtoUrl = `mailto:${toEmail}?subject=${encodeURIComponent(
      formData.subject
    )}&body=${encodeURIComponent(emailBody)}`;
    console.log("to email", mailtoUrl);
    window.location.href = mailtoUrl;

    toast({
      title: "Opening Email Client",
      description:
        "Your default email application should open with the message ready to send.",
    });

    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      subject: "",
      message: "",
    });
  };

  return (
    <Layout>
      <section className="relative flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-blue-900/80 z-10"></div>
          <img
            src={heroImage?.image_url}
            alt={heroImage?.alt_text || ""}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 z-20 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-30 text-center pt-24 pb-24">
          <div className="max-w-4xl mx-auto animate-on-scroll">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Contact Us
            </h1>
            <p className="text-xl text-slate-200">
              Get in touch with our team to discuss your IT requirements.
            </p>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
          <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="bg-goeltec-lightblue p-8 rounded-lg animate-on-scroll">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>

                <div className="space-y-6">
                  {getEmails().map((email) => {
                    let IconComponent =
                      email.icon_name &&
                      icons[email.icon_name as keyof typeof icons]
                        ? icons[email.icon_name as keyof typeof icons]
                        : Mail;

                    return (
                      <ContactInfoItem
                        key={email.id}
                        icon={
                          email.custom_icon_url ? (
                            <img
                              src={email.custom_icon_url}
                              alt={`${email.label} icon`}
                              className="w-6 h-6"
                            />
                          ) : (
                            <IconComponent
                              size={24}
                              className="text-goeltec-blue"
                            />
                          )
                        }
                        title={email.label}
                        content={
                          <a
                            href={`mailto:${email.value}`}
                            className="hover:text-goeltec-blue transition-colors"
                          >
                            {email.value}
                          </a>
                        }
                      />
                    );
                  })}

                  {getPhones().map((phone) => {
                    let IconComponent =
                      phone.icon_name &&
                      icons[phone.icon_name as keyof typeof icons]
                        ? icons[phone.icon_name as keyof typeof icons]
                        : Phone;

                    return (
                      <ContactInfoItem
                        key={phone.id}
                        icon={
                          phone.custom_icon_url ? (
                            <img
                              src={phone.custom_icon_url}
                              alt={`${phone.label} icon`}
                              className="w-6 h-6"
                            />
                          ) : (
                            <IconComponent
                              size={24}
                              className="text-goeltec-blue"
                            />
                          )
                        }
                        title={phone.label}
                        content={phone.value}
                      />
                    );
                  })}

                  {getAddresses().map((address) => {
                    let IconComponent =
                      address.icon_name &&
                      icons[address.icon_name as keyof typeof icons]
                        ? icons[address.icon_name as keyof typeof icons]
                        : MapPin;

                    return (
                      <ContactInfoItem
                        key={address.id}
                        icon={
                          address.custom_icon_url ? (
                            <img
                              src={address.custom_icon_url}
                              alt={`${address.label} icon`}
                              className="w-6 h-6"
                            />
                          ) : (
                            <IconComponent
                              size={24}
                              className="text-goeltec-blue"
                            />
                          )
                        }
                        title={address.label}
                        content={address.value}
                      />
                    );
                  })}
                </div>

                {getSocialLinks().length > 0 && (
                  <div className="mt-8 pt-6 border-t border-blue-100">
                    <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
                    <div className="space-y-3">
                      {getSocialLinks().map((social) => {
                        let IconComponent =
                          social.icon_name &&
                          icons[social.icon_name as keyof typeof icons]
                            ? icons[social.icon_name as keyof typeof icons]
                            : Mail;

                        return (
                          <ContactInfoItem
                            key={social.id}
                            icon={
                              social.custom_icon_url ? (
                                <img
                                  src={social.custom_icon_url}
                                  alt={`${social.label} icon`}
                                  className="w-6 h-6"
                                />
                              ) : (
                                <IconComponent
                                  size={24}
                                  className="text-goeltec-blue"
                                />
                              )
                            }
                            title={social.label}
                            content={
                              <a
                                href={social.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-goeltec-blue transition-colors"
                              >
                                {social.value}
                              </a>
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-lg shadow-md animate-on-scroll">
                <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>

                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                        required
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message *{" "}
                      <span className="text-gray-400 text-xs">
                        ({formData.message.length}/{MAX_MESSAGE_LENGTH})
                      </span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-goeltec-blue"
                      maxLength={MAX_MESSAGE_LENGTH}
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="px-6 py-3 bg-goeltec-blue text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <Send size={18} className="mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const ContactInfoItem = ({
  icon,
  title,
  content,
}: {
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}) => (
  <div className="flex items-start">
    <div className="flex-shrink-0 mt-1">{icon}</div>
    <div className="ml-4">
      <h3 className="text-lg font-medium">{title}</h3>
      <div className="mt-1 text-gray-700">{content}</div>
    </div>
  </div>
);

export default Contact;
